import Icon from "@components/UI/Icon";
import Tooltip from "@components/UI/Tooltip";
import React from "react";
import { twMerge } from "tailwind-merge";

import { PaymentPlan } from "../paymentPlansData";
import { BUSINESS_PLAN, PRICE_PLANS } from "../plansData";
import { StartButton } from "../StartButton";
import { FEATURES } from "./featuresData";

const Check = () => {
  return (
    <Icon
      name="present"
      color={["#6A6FD7", "#FFFFFF"]}
      size={16}
    />
  )
}

const NotPresent = () => {
  return (
    <Icon
      name="not-present"
      color={["#FF7E6D", "#FFFFFF"]}
      size={16}
    />
  )
}

const FeatureValue = ({ value }) => {
  if (value.type === "present") {
    return <Check />
  }

  if (value.type === "notPresent") {
    return <NotPresent />
  }

  return value.content
}

const mobileHeaderCellClassName = "align-middle text-center text-base font-semibold uppercase text-neutral2-900"

const mobileValueCellClassName = "bg-white h-[58px] align-middle p-0 text-center text-[10px] leading-[13px] text-neutral2-900"

export type FeaturesTableMobileProps = {
  className?: string
  paymentPlan: PaymentPlan
}

export function FeaturesTableMobile({ className, paymentPlan }: FeaturesTableMobileProps) {
  return (
    <div className={twMerge("w-full flex justify-center", className)}>
      <div className="relative w-[800px] pt-[128px]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-[200px] top-0 z-20"
        >
          <div
            className="sticky top-[72px] grid h-[128px]"
            style={{ gridTemplateColumns: `repeat(${PRICE_PLANS.length}, minmax(0, 1fr))` }}
          >
            {PRICE_PLANS.map((plan, planIndex) => (
              <div
                key={plan.key}
                className={twMerge(
                  mobileHeaderCellClassName,
                  "flex items-center justify-center bg-white border-b-[1px] border-slate-blue-150 px-[6px] py-[12px]",
                  planIndex === 0 && "rounded-tl-xl",
                  planIndex === PRICE_PLANS.length - 1 && "rounded-tr-xl",
                  planIndex !== 0 && "border-l-[1px]",
                  plan.recommended && "bg-slate-blue-400 text-white"
                )}
              >
                <span className="mx-auto inline-flex items-center justify-center whitespace-nowrap [writing-mode:sideways-lr]">
                  {plan.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <table className="w-full table-fixed border-separate border-spacing-0">
          <colgroup>
            {PRICE_PLANS.map((plan) => (
              <col key={plan.key} style={{ width: `${100 / PRICE_PLANS.length}%` }} />
            ))}
          </colgroup>

          {FEATURES.map((scope, scopeIndex) => (
            <tbody key={scopeIndex}>
              {scope.scopeTitle && (
                <tr>
                  <th
                    colSpan={PRICE_PLANS.length}
                    scope="rowgroup"
                    className="h-[42px] align-middle px-[16px] py-[11px] text-center text-[12px] leading-[16px] font-semibold text-neutral2-900"
                  >
                    {scope.scopeTitle}
                  </th>
                </tr>
              )}

              {scope.features.map((feature, featureIndex) => (
                <React.Fragment key={featureIndex}>
                  <tr>
                    <th
                      colSpan={PRICE_PLANS.length}
                      scope="row"
                      className="bg-white border-b-[1px] border-transparent bg-clip-padding h-[42px] align-middle px-[16px] py-[10px] text-center text-[11px] leading-[16px] font-normal text-neutral2-900"
                    >
                      <span className="inline-flex items-center justify-center gap-[6px]">
                        <span>{feature.title}</span>
                        {feature.description && (
                          <Tooltip
                            title={feature.description}
                            triggerLabel={`Show ${feature.title} details`}
                          >
                            <Icon
                              name="info-circle-outlined"
                              color="neutral2-400"
                              size={12}
                            />
                          </Tooltip>
                        )}
                      </span>
                    </th>
                  </tr>

                  <tr className="">
                    {PRICE_PLANS.map((plan) => (
                      <td
                        key={plan.key}
                        className={twMerge(mobileValueCellClassName,
                          "bg-white bg-clip-padding border-b-[1px] border-transparent"
                        )}
                      >
                        <div className="flex h-[58px] w-full items-center justify-center px-[6px] py-[10px]">
                          <FeatureValue value={feature.byPlan[plan.key]} />
                        </div>
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          ))}
          <tfoot>
            <tr>
              <td
                colSpan={PRICE_PLANS.length}
                className="h-[82px] bg-white rounded-b-xl"
              />
            </tr>
          </tfoot>
        </table>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[300px] z-20 flex flex-col">
          <div className="flex-1" />
          <div className="pointer-events-auto sticky bottom-0 mx-auto py-[12px]">
            <StartButton
              className="w-[157px] !py-[18px] shadow-[0px_4px_20px_0px_#6A6FD733]"
              paymentPlan={paymentPlan}
              plan={BUSINESS_PLAN}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
